import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { format, parseISO, set } from "date-fns";
import { Form, Formik } from "formik";
import React from "react";
import { DateObject, Calendar as MultiCalendar } from "react-multi-date-picker";
import { useDispatch, useSelector } from "react-redux";
import { useLocalStorage } from "../hooks";
import { useToast } from "../provider/ToastProvider";
import { loadingState, setActionLoading, setGlobalLoading } from "../redux/reducers/loadingReducer";
import "../styles/Calendar.css";
import axiosInstance from "../utils/axiosInstance";
import { getUrlForClasses, getUrlForEvent } from "../utils/confighelpers";
import { TwButton, TwFormHelperText, TwTimePicker } from "./tailwind";

export default function ChangeDateTimeModal({
  open,
  handleClose,
  sessionData,
  instanceId,
  handleUpdate,
}) {
  const { addToast, addSuccessToast, addErrorToast } = useToast();
  const dispatch = useDispatch();
  const loadingReduxState = useSelector(loadingState);
  const [userToken] = useLocalStorage("userToken");
  const [sessionDetails, setSessionDetails] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      if (open) {
        const sessionDetailsConfig = {
          method: "get",
          url: `${(sessionData.hasOwnProperty("eventId") ? `${getUrlForEvent()}changeEventScheduleDetail` : `${getUrlForClasses()}changeScheduleDetail`)}?reqId=${sessionData._id}&type=${sessionData.isPackage ? "package" : "instance"
            }&instanceId=${instanceId}`,
          headers: {
            "x-access-token": userToken,
          },
        };
        dispatch(setGlobalLoading(true));
        const sessionDetailsRequest = await axiosInstance(sessionDetailsConfig);
        dispatch(setGlobalLoading(false));
        setSessionDetails(sessionDetailsRequest?.data?.data);
      }
    })();
  }, [instanceId, sessionData]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="relative z-50"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="absolute right-0 top-0 pr-4 pt-4 sm:block">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div className="sm:flex sm:items-center">
              <div className="mt-3 text-center sm:mx-4 sm:mt-0 sm:text-left w-full">
                <DialogTitle as="h3" className="text-xl font-semibold text-gray-900">
                  Change Schedule
                </DialogTitle>
                <div className="mt-2">
                  <Formik
                    initialValues={{
                      classDate: parseISO(sessionData.hasOwnProperty("eventId") ? sessionData.eventDate : sessionData.classDate),
                      startTime: parseISO(sessionData.hasOwnProperty("eventId") ? sessionData.eventDate : sessionData.classDate),
                    }}
                    validate={(values) => {
                      const errors = {};
                      if (!values.classDate) {
                        errors.classDate = "Required!";
                      }
                      if (!values.startTime) {
                        errors.startTime = "Required!";
                      }
                      return errors;
                    }}
                    onSubmit={async (values, { setSubmitting }) => {
                      setSubmitting(true);

                      const classDateTransformed =
                        values.classDate instanceof DateObject
                          ? values.classDate.toDate()
                          : values.classDate;

                      let startDate = set(values.classDate, {
                        hours: Number(format(values.startTime, "k")),
                        minutes: Number(format(values.startTime, "m")),
                      })
                      const body = {
                        // isPackage: sessionData.isPackage,
                        isPackage: false,
                        // scheduleId: sessionDetails._id,
                        scheduleId: instanceId,
                        ...(sessionData.hasOwnProperty("eventId") ? { eventId: sessionData.eventId._id, eventScheduleId: instanceId, eventDate: startDate } : { classId: sessionData.classId._id, scheduleId: instanceId, classDate: startDate }),
                        meridiem: format(values.startTime, "a"),
                        timing: format(values.startTime, "KK:mm a"),
                        // classDate: set(values.classDate, {
                        //   hours: Number(format(values.startTime, "k")),
                        //   minutes: Number(format(values.startTime, "m")),
                        // }),
                      };
                      const config = {
                        method: "put",
                        url: `${(sessionData.hasOwnProperty("eventId") ? `${getUrlForEvent()}schedule/update` : `${getUrlForClasses()}updateScheduleTheClass`)}`,
                        headers: {
                          "x-access-token": userToken,
                        },
                        data: body,
                      };
                      // console.log("classDateTransformed", classDateTransformed);
                      // console.log("body", body);
                      dispatch(setActionLoading(true));
                      const request = await axiosInstance(config);
                      dispatch(setActionLoading(false));
                      if (request.status === 200) {
                        if (request.data.statusCode === 200) {
                          addSuccessToast("Success!");
                          setSubmitting(false);
                          handleClose();
                          handleUpdate();
                        } else {
                          setSubmitting(false);
                          addErrorToast(request.data.message);
                        }
                      }
                    }}
                  >
                    {({ values, errors, setFieldValue, submitForm, isSubmitting }) => (
                      <Form className="flex flex-col gap-4 w-full">
                        <MultiCalendar
                          className='bookzy-calendar bookzy-border px-2.5 py-1'
                          minDate={new Date()}
                          value={values.classDate}
                          onChange={(date) => {
                            const dateSelected = date.toDate();
                            setFieldValue("classDate", dateSelected);
                          }}
                          weekDays={["S", "M", "T", "W", "T", "F", "S"]}
                          mapDays={({
                            date,
                            today,
                            selectedDate,
                            currentMonth,
                            isSameDate,
                          }) => {
                            let props = {};


                            if (isSameDate(date, today)) {
                              props.className = "default-current-date";
                              props.style = {
                                ...props.style,
                                border: "1px solid #777",
                              };
                            }
                            if (isSameDate(date, selectedDate)) {
                              props.className = "selected-date";
                            }

                            return props;
                          }}
                        />
                        {errors.classDate && (
                          <TwFormHelperText error={true}>{errors.classDate}</TwFormHelperText>
                        )}
                        <TwTimePicker
                          label={"Select Start Time"}
                          name={"startTime"}
                          format={"HH:mm"}
                          rightIcon={(
                            <svg className={"size-5 sm:size-4"} width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M6.11692 12.978C2.73828 12.978 0 10.2397 0 6.86106C0 3.48242 2.73828 0.744141 6.11692 0.744141C9.49556 0.744141 12.2338 3.48242 12.2338 6.86106C12.2338 10.2397 9.49556 12.978 6.11692 12.978ZM5.54346 6.86106C5.54346 7.05221 5.63904 7.23142 5.79913 7.31744L8.09297 8.84667C8.35581 9.0426 8.71183 8.97092 8.86714 8.70808C9.06308 8.44525 8.99139 8.08922 8.72856 7.91241L6.69038 6.55521V3.61145C6.69038 3.29365 6.43471 3.03799 6.09542 3.03799C5.79913 3.03799 5.52195 3.29365 5.52195 3.61145L5.54346 6.86106Z" fill="#000" />
                            </svg>)}
                          value={values.startTime}
                          onChange={(date, dateParams) => {
                            setFieldValue("startTime", date.toDate());
                          }}
                          errors={errors} />
                        <TwButton
                          variant={"orange"}
                          onClick={submitForm}
                          disabled={loadingReduxState.actionLoading}
                          actionLoading={(loadingReduxState.actionLoading)}
                        >
                          Update
                        </TwButton>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
